import { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import DoneRoundedIcon from '@mui/icons-material/DoneRounded'
import dayjs, { type Dayjs }  from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/cs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import { useRecordYearlyFeePayment } from '@/hooks/api/use-yearly-fee-payment'

function PaidDialog({
    yearlyFeePaymentId,
    handleClose,
}: {
    yearlyFeePaymentId: number,
    handleClose: () => void,
}) {

    const [requestInProgress, setRequestInProgress] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)

    const [paidAt, setPaidAt] = useState<Dayjs>(dayjs())

    const mutation = useRecordYearlyFeePayment(
        yearlyFeePaymentId,
        {
            setRequestInProgress,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            },
            onSuccessCallback() {
                handleClose()
            },
        },
    )

    const handleClickDelete = () => {
        setRequestInProgress(true)
        mutation.mutate(paidAt)
    }

    return (
        <Box sx={{
            minWidth: 200,
            minHeight: 100,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
        }}>
            {error &&
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 2,
                    px: 3,
                }}>
                    <ErrorRoundedIcon sx={{
                        color: 'error.main',
                        fontSize: '4rem',
                    }}/>
                    <Typography variant="body1" component="div">
                        Platbu se nepodařilo označit jako uhrazenou:
                    </Typography>
                    <Typography variant="body2" component="div">
                        {error}
                    </Typography>
                    <Button
                        variant="contained"
                        color="terciary"
                        size="small"
                        onClick={() => {
                            setError(null)
                            handleClose()
                        }}
                    >
                        {'Zavřít'}
                    </Button>
                </Box>
            }
            {!error &&
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <Typography variant="h5" component="div" mb={2}>
                        Označit platbu ID: {yearlyFeePaymentId} jako uhrazenou
                    </Typography>
                    <Box p={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="cs">
                            <DatePicker
                                label="Datum úhrady"
                                views={['year', 'month', 'day']}
                                value={paidAt}
                                onChange={newDate => {
                                    setPaidAt(newDate)
                                }}
                            />
                        </LocalizationProvider>
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexGrow: 1,
                    }}>
                        <Button
                            variant="text"
                            color="terciary"
                            onClick={() => handleClose()}
                        >
                            Zrušit
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={requestInProgress ? <CircularProgress color="inherit" size={20} /> : <DoneRoundedIcon />}
                            onClick={() => handleClickDelete()}
                            disabled={requestInProgress}
                        >
                            Uhrazeno
                        </Button>
                    </Box>
                </Box>
            }
        </Box>
    )
}

export default PaidDialog
